'use client';


import NextIcon_default from "../../icons/NextIcon.js";
import PrevIcon_default from "../../icons/PrevIcon.js";
import { Box } from "../Layout/Box/Box.js";
import { Menu, MenuButton, MenuCheckboxItem, MenuDivider, MenuGroup, MenuItem, MenuItemOption, MenuList, MenuOptionGroup, StylesProvider, useStyles } from "./Dropdown.base.js";
import { forwardRef, useMenuButton, useMenuContext, useMenuDescendantsContext, useMultiStyleConfig, useTheme } from "@chakra-ui/react";
import { c } from "react-compiler-runtime";
import { merge } from "lodash";
import { jsx, jsxs } from "react/jsx-runtime";
import { createContext } from "@chakra-ui/react-utils";

//#region src/components/Dropdown/Dropdown.tsx
const openingKeysInRtl = ["ArrowLeft", "Enter"];
const openingKeysInLtr = ["ArrowRight", "Enter"];
const closingKeysInRtl = ["ArrowRight"];
const closingKeysInLtr = ["ArrowLeft"];
/**
* Get the normalized event key across all browsers
* @param event keyboard event
*/
function normalizeEventKey(event) {
	const { key, keyCode } = event;
	const isArrowKey = keyCode >= 37 && keyCode <= 40 && key.indexOf("Arrow") !== 0;
	const eventKey = isArrowKey ? `Arrow${key}` : key;
	return eventKey;
}
const [NestedDropdownContextProvider, useNestedDropdownContext] = createContext({ name: "NestedDropdownContext" });
const HoverBox = (t0) => {
	const $ = c(8);
	let children;
	let rest;
	if ($[0] !== t0) {
		({children, ...rest} = t0);
		$[0] = t0;
		$[1] = children;
		$[2] = rest;
	} else {
		children = $[1];
		rest = $[2];
	}
	const menu = useMenuContext();
	let t1;
	if ($[3] !== children || $[4] !== menu.onClose || $[5] !== menu.onOpen || $[6] !== rest) {
		t1 = /* @__PURE__ */ jsx(Box, {
			...rest,
			onMouseEnter: menu.onOpen,
			onMouseLeave: menu.onClose,
			children
		});
		$[3] = children;
		$[4] = menu.onClose;
		$[5] = menu.onOpen;
		$[6] = rest;
		$[7] = t1;
	} else t1 = $[7];
	return t1;
};
const NestedDropdownButton = forwardRef((props, ref) => {
	const $ = c(15);
	let children;
	let rest;
	if ($[0] !== props) {
		({children, ...rest} = props);
		$[0] = props;
		$[1] = children;
		$[2] = rest;
	} else {
		children = $[1];
		rest = $[2];
	}
	const buttonProps = useMenuButton(props, ref);
	const menu = useMenuContext();
	const { openingKeys, closingKeys } = useNestedDropdownContext();
	const { direction } = useTheme();
	let t0;
	if ($[3] !== closingKeys || $[4] !== menu || $[5] !== openingKeys) {
		t0 = (event) => {
			const normalizedKey = normalizeEventKey(event);
			if (openingKeys.includes(normalizedKey)) {
				event.preventDefault();
				menu.onOpen();
				setTimeout(() => {
					menu.setFocusedIndex(1);
				}, 10);
			} else if (closingKeys.includes(normalizedKey)) {
				event.preventDefault();
				menu.onClose();
			}
		};
		$[3] = closingKeys;
		$[4] = menu;
		$[5] = openingKeys;
		$[6] = t0;
	} else t0 = $[6];
	const onKeyDown = t0;
	let t1;
	if ($[7] !== direction) {
		t1 = direction === "ltr" ? /* @__PURE__ */ jsx(NextIcon_default, { ms: "auto" }) : /* @__PURE__ */ jsx(PrevIcon_default, { ms: "auto" });
		$[7] = direction;
		$[8] = t1;
	} else t1 = $[8];
	let t2;
	if ($[9] !== buttonProps || $[10] !== children || $[11] !== onKeyDown || $[12] !== rest || $[13] !== t1) {
		t2 = /* @__PURE__ */ jsxs(MenuItem, {
			...rest,
			...buttonProps,
			onKeyDownCapture: onKeyDown,
			display: "flex",
			alignItems: "center",
			pe: "2",
			children: [children, t1]
		});
		$[9] = buttonProps;
		$[10] = children;
		$[11] = onKeyDown;
		$[12] = rest;
		$[13] = t1;
		$[14] = t2;
	} else t2 = $[14];
	return t2;
});
const Dropdown = (props) => {
	const $ = c(27);
	let children;
	let rest;
	if ($[0] !== props) {
		({children, ...rest} = props);
		$[0] = props;
		$[1] = children;
		$[2] = rest;
	} else {
		children = $[1];
		rest = $[2];
	}
	const parentMenu = useMenuContext();
	const isNested = !!parentMenu && !props.ignoreNesting;
	const { direction } = useTheme();
	let t0;
	if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
		t0 = {};
		$[3] = t0;
	} else t0 = $[3];
	const styles = useMultiStyleConfig("Menu", t0);
	let t1;
	const t2 = direction === "rtl" ? openingKeysInRtl : openingKeysInLtr;
	const t3 = direction === "rtl" ? closingKeysInRtl : closingKeysInLtr;
	let t4;
	if ($[4] !== isNested || $[5] !== t2 || $[6] !== t3) {
		t4 = {
			isNested,
			openingKeys: t2,
			closingKeys: t3
		};
		$[4] = isNested;
		$[5] = t2;
		$[6] = t3;
		$[7] = t4;
	} else t4 = $[7];
	t1 = t4;
	const contextValue = t1;
	let t5;
	if ($[8] !== isNested) {
		t5 = isNested ? [2, -4] : [0, 0];
		$[8] = isNested;
		$[9] = t5;
	} else t5 = $[9];
	const t6 = isNested ? "end-start" : "bottom-end";
	let t7;
	if ($[10] !== rest.boundary) {
		t7 = [{
			name: "preventOverflow",
			options: {
				mainAxis: true,
				altAxis: true,
				boundary: rest.boundary
			}
		}];
		$[10] = rest.boundary;
		$[11] = t7;
	} else t7 = $[11];
	let t8;
	if ($[12] !== children || $[13] !== isNested) {
		t8 = isNested ? /* @__PURE__ */ jsx(HoverBox, { children }) : children;
		$[12] = children;
		$[13] = isNested;
		$[14] = t8;
	} else t8 = $[14];
	let t9;
	if ($[15] !== contextValue || $[16] !== t8) {
		t9 = /* @__PURE__ */ jsx(NestedDropdownContextProvider, {
			value: contextValue,
			children: t8
		});
		$[15] = contextValue;
		$[16] = t8;
		$[17] = t9;
	} else t9 = $[17];
	let t10;
	if ($[18] !== styles || $[19] !== t9) {
		t10 = /* @__PURE__ */ jsx(StylesProvider, {
			value: styles,
			children: t9
		});
		$[18] = styles;
		$[19] = t9;
		$[20] = t10;
	} else t10 = $[20];
	let t11;
	if ($[21] !== rest || $[22] !== t10 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
		t11 = /* @__PURE__ */ jsx(Menu, {
			gutter: 0,
			offset: t5,
			placement: t6,
			modifiers: t7,
			...rest,
			children: t10
		});
		$[21] = rest;
		$[22] = t10;
		$[23] = t5;
		$[24] = t6;
		$[25] = t7;
		$[26] = t11;
	} else t11 = $[26];
	return t11;
};
const DropdownList = (props) => {
	const $ = c(16);
	const styles = useStyles();
	let t0;
	if ($[0] !== props.sx || $[1] !== styles.list) {
		t0 = merge(styles.list, props.sx);
		$[0] = props.sx;
		$[1] = styles.list;
		$[2] = t0;
	} else t0 = $[2];
	const sx = t0;
	const { nextEnabled, prevEnabled, enabledCount } = useMenuDescendantsContext();
	const { isNested, closingKeys } = useNestedDropdownContext();
	const menu = useMenuContext();
	let t1;
	if ($[3] !== closingKeys || $[4] !== enabledCount || $[5] !== menu || $[6] !== nextEnabled || $[7] !== prevEnabled) {
		t1 = (event) => {
			const normalizedKey = normalizeEventKey(event);
			if (normalizedKey === "ArrowDown") {
				event.preventDefault();
				const next = nextEnabled(menu.focusedIndex);
				if (next) {
					let nextIndex = next.index;
					if (nextIndex === 0) nextIndex = 1;
					menu.setFocusedIndex(nextIndex);
				}
			} else if (normalizedKey === "ArrowUp") {
				event.preventDefault();
				const prev = prevEnabled(menu.focusedIndex);
				if (prev) {
					let prevIndex = prev.index;
					if (prevIndex === 0) prevIndex = enabledCount() - 1;
					menu.setFocusedIndex(prevIndex);
				}
			} else if (closingKeys.includes(normalizedKey)) {
				event.preventDefault();
				menu.onClose();
			}
		};
		$[3] = closingKeys;
		$[4] = enabledCount;
		$[5] = menu;
		$[6] = nextEnabled;
		$[7] = prevEnabled;
		$[8] = t1;
	} else t1 = $[8];
	const nestedListOnKeyDown = t1;
	const t2 = props.bare && styles.bareList;
	let t3;
	if ($[9] !== sx || $[10] !== t2) {
		t3 = {
			...sx,
			...t2
		};
		$[9] = sx;
		$[10] = t2;
		$[11] = t3;
	} else t3 = $[11];
	const t4 = isNested ? nestedListOnKeyDown : void 0;
	let t5;
	if ($[12] !== props || $[13] !== t3 || $[14] !== t4) {
		t5 = /* @__PURE__ */ jsx(MenuList, {
			sx: t3,
			...props,
			onKeyDown: t4
		});
		$[12] = props;
		$[13] = t3;
		$[14] = t4;
		$[15] = t5;
	} else t5 = $[15];
	return t5;
};
const DropdownButton = forwardRef((props, ref) => {
	const $ = c(12);
	let As;
	let rest;
	let variant;
	if ($[0] !== props) {
		({variant, as: As, ...rest} = props);
		$[0] = props;
		$[1] = As;
		$[2] = rest;
		$[3] = variant;
	} else {
		As = $[1];
		rest = $[2];
		variant = $[3];
	}
	const buttonVariant = variant ?? "outline";
	const { isNested } = useNestedDropdownContext();
	if (isNested) {
		let t0$1;
		if ($[4] !== ref || $[5] !== rest) {
			t0$1 = /* @__PURE__ */ jsx(NestedDropdownButton, {
				ref,
				...rest
			});
			$[4] = ref;
			$[5] = rest;
			$[6] = t0$1;
		} else t0$1 = $[6];
		return t0$1;
	}
	let t0;
	if ($[7] !== As || $[8] !== buttonVariant || $[9] !== ref || $[10] !== rest) {
		t0 = /* @__PURE__ */ jsx(MenuButton, {
			ref,
			variant: buttonVariant,
			as: As,
			...rest
		});
		$[7] = As;
		$[8] = buttonVariant;
		$[9] = ref;
		$[10] = rest;
		$[11] = t0;
	} else t0 = $[11];
	return t0;
});
const DropdownDivider = () => {
	const $ = c(2);
	const styles = useStyles();
	let t0;
	if ($[0] !== styles.divider) {
		t0 = /* @__PURE__ */ jsx(MenuDivider, { sx: styles.divider });
		$[0] = styles.divider;
		$[1] = t0;
	} else t0 = $[1];
	return t0;
};
const DropdownItem = (props) => {
	const $ = c(2);
	let t0;
	if ($[0] !== props) {
		t0 = /* @__PURE__ */ jsx(MenuItem, { ...props });
		$[0] = props;
		$[1] = t0;
	} else t0 = $[1];
	return t0;
};
const DropdownOptionGroup = MenuOptionGroup;
const DropdownItemOption = MenuItemOption;
const DropdownCheckboxItem = MenuCheckboxItem;
const DropdownGroup = MenuGroup;
Dropdown.List = DropdownList;
Dropdown.Divider = DropdownDivider;
Dropdown.Button = DropdownButton;
Dropdown.Item = DropdownItem;
Dropdown.OptionGroup = DropdownOptionGroup;
Dropdown.ItemOption = DropdownItemOption;
Dropdown.CheckboxItem = DropdownCheckboxItem;
Dropdown.Group = DropdownGroup;

//#endregion
export { Dropdown };
//# sourceMappingURL=Dropdown.js.map